import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const SWATCHES = [
    { name: "Ink", hex: "#0A0A0A", cls: "bg-ink" },
    { name: "Paper", hex: "#F4F4F0", cls: "bg-paper border border-ink/10" },
    { name: "WorX Orange", hex: "#FF4500", cls: "bg-worx" },
];

const CARDS = [
    {
        title: "Business card — Front",
        src: "/assets/business-card-front.png",
        testid: "front",
        note: "Upload this side as the front when ordering.",
    },
    {
        title: "Business card — Back",
        src: "/assets/business-card-back.png",
        testid: "back",
        note: "Upload this side as the back when ordering.",
    },
];

const signatureHtml = (origin) => `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;max-width:480px">
  <tr>
    <td style="padding-right:14px;vertical-align:top">
      <img src="${origin}/assets/email-headshot.png" width="84" height="84" alt="Alex Hudson" style="display:block;border-radius:50%;border:2px solid #FF4500" />
    </td>
    <td style="vertical-align:top">
      <div style="font-size:17px;font-weight:bold;color:#0A0A0A;letter-spacing:0.3px">Alex Hudson</div>
      <div style="font-size:11px;color:#FF4500;font-weight:bold;letter-spacing:1.5px;text-transform:uppercase;padding-top:3px">Founder &amp; Developer</div>
      <div style="font-size:13px;color:#3a3a3a;padding-top:8px;line-height:1.65">
        561-210-0718<br />
        <a href="mailto:alex.hudson@cworx.biz" style="color:#0A0A0A;text-decoration:none">alex.hudson@cworx.biz</a><br />
        <a href="https://cworx.biz" style="color:#0A0A0A;text-decoration:none;font-weight:bold">cworx.biz</a>
      </div>
    </td>
    <td style="vertical-align:middle;padding-left:16px">
      <img src="${origin}/assets/email-logo.png" width="110" alt="Consulting WorX" style="display:block" />
    </td>
  </tr>
  <tr>
    <td colspan="3" style="padding-top:12px">
      <div style="border-top:2px solid #FF4500;padding-top:8px;font-size:12px;color:#6b6b6b">
        Websites, Web Apps &amp; Custom CRM — <a href="https://cworx.biz/#booking" style="color:#FF4500;font-weight:bold;text-decoration:none">Book a free discovery call</a>
      </div>
    </td>
  </tr>
</table>`;

export default function Brand() {
    const navigate = useNavigate();
    const authed = !!sessionStorage.getItem("cw_admin_token");

    useEffect(() => {
        if (!authed) navigate("/admin", { replace: true });
    }, [authed, navigate]);

    if (!authed) return null;

    const copySignature = async () => {
        const html = signatureHtml("https://cworx.biz");
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    "text/html": new Blob([html], { type: "text/html" }),
                    "text/plain": new Blob([html], { type: "text/plain" }),
                }),
            ]);
            toast.success("Signature copied — paste it into your Gmail signature settings.");
        } catch {
            const el = document.getElementById("sig-preview");
            const range = document.createRange();
            range.selectNodeContents(el);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            document.execCommand("copy");
            sel.removeAllRanges();
            toast.success("Signature copied — paste it into your Gmail signature settings.");
        }
    };

    return (
        <div className="bg-paper text-ink min-h-screen" data-testid="brand-page">
            <header className="border-b border-ink/10">
                <div className="mx-auto max-w-7xl px-5 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/admin" data-testid="brand-back-link" className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-worx transition-colors">
                        <ArrowLeft size={16} /> Back to console
                    </Link>
                    <span className="font-display font-semibold tracking-tight">Consulting WorX — Brand</span>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-ink/50">
                        <span className="h-px w-10 bg-worx inline-block" /> Brand identity
                    </p>
                    <h1 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-5xl md:text-7xl">
                        The mark of the WorX.
                    </h1>
                </Reveal>

                <Reveal className="mt-14">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-medium text-2xl">Primary logo</h2>
                        <a href="/assets/logo.png" download data-testid="download-logo-btn" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-worx transition-colors">
                            <Download size={14} /> Download PNG
                        </a>
                    </div>
                    <div className="border border-ink/10 bg-white flex items-center justify-center p-10 md:p-20">
                        <img src="/assets/logo.png" alt="Consulting WorX logo" data-testid="brand-logo-image" className="w-full max-w-md" />
                    </div>
                </Reveal>

                <div className="mt-16">
                    <h2 className="font-display font-medium text-2xl mb-2">Business cards — print files</h2>
                    <p className="text-sm text-ink/50 mb-6">
                        Preview each side below and download the PNG. Upload them as front and back at any printer
                        (Vistaprint, MOO, etc.). Standard card size is 3.5″ × 2″.
                    </p>
                    <div className="grid md:grid-cols-2 gap-8">
                        {CARDS.map((c, i) => (
                            <Reveal key={c.testid} delay={i * 0.08}>
                                <div className="border border-ink/10 bg-white">
                                    <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
                                        <h3 className="font-display font-medium text-lg">{c.title}</h3>
                                        <a
                                            href={c.src}
                                            download
                                            data-testid={`download-card-${c.testid}-btn`}
                                            className="inline-flex items-center gap-1.5 bg-ink text-paper px-4 py-2 text-sm hover:bg-worx transition-colors"
                                        >
                                            <Download size={14} /> Download
                                        </a>
                                    </div>
                                    <div className="p-6 md:p-10 bg-[#EAE8E0] flex items-center justify-center">
                                        <img
                                            src={c.src}
                                            alt={c.title}
                                            data-testid={`brand-card-${c.testid}-image`}
                                            className="w-full shadow-[0_18px_40px_rgba(10,10,10,0.25)]"
                                        />
                                    </div>
                                    <p className="px-6 py-4 text-sm text-ink/50 border-t border-ink/10">{c.note}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal className="mt-16">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-medium text-2xl">Card mockup</h2>
                        <a href="/assets/business-card.png" download data-testid="download-card-btn" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-worx transition-colors">
                            <Download size={14} /> Download PNG
                        </a>
                    </div>
                    <div className="border border-ink/10 overflow-hidden">
                        <img src="/assets/business-card.png" alt="Consulting WorX business card mockup" data-testid="brand-card-image" className="w-full object-cover" />
                    </div>
                </Reveal>

                <Reveal className="mt-16">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-medium text-2xl">Email signature</h2>
                        <div className="flex items-center gap-4">
                            <a href="/assets/email-signature.html" download data-testid="download-signature-btn" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-worx transition-colors">
                                <Download size={14} /> Download HTML
                            </a>
                            <button
                                onClick={copySignature}
                                data-testid="copy-signature-btn"
                                className="inline-flex items-center gap-1.5 bg-ink text-paper px-4 py-2 text-sm hover:bg-worx transition-colors"
                            >
                                <Copy size={14} /> Copy signature
                            </button>
                        </div>
                    </div>
                    <div className="border border-ink/10 bg-white p-8 md:p-12">
                        <div id="sig-preview" data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: signatureHtml("") }} />
                    </div>
                    <div className="mt-4 text-sm text-ink/60 space-y-1.5 max-w-2xl" data-testid="signature-instructions">
                        <p className="font-medium text-ink">Install it in Google Workspace (cworx.biz mailbox):</p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Click <strong>Copy signature</strong> above.</li>
                            <li>In Gmail: gear icon → <strong>See all settings</strong> → <strong>General</strong> → scroll to <strong>Signature</strong>.</li>
                            <li>Create new signature, name it, then paste (Ctrl/Cmd+V) into the editor and save.</li>
                        </ol>
                        <p className="text-ink/40 text-xs pt-1">Images load from cworx.biz — they appear in emails once the latest site publish is live.</p>
                    </div>
                </Reveal>

                <div className="mt-16 grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                    <Reveal>
                        <div className="bg-white p-8 md:p-12 h-full">
                            <h2 className="font-display font-medium text-2xl mb-6">Palette</h2>
                            <div className="space-y-3">
                                {SWATCHES.map((s) => (
                                    <div key={s.name} data-testid={`swatch-${s.name.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-4">
                                        <span className={`h-12 w-12 ${s.cls}`} />
                                        <div>
                                            <p className="font-medium text-sm">{s.name}</p>
                                            <p className="text-xs text-ink/50 font-mono">{s.hex}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="bg-white p-8 md:p-12 h-full">
                            <h2 className="font-display font-medium text-2xl mb-6">Typography</h2>
                            <p className="font-display font-semibold text-4xl tracking-tighter">Clash Display</p>
                            <p className="text-xs text-ink/50 mt-1 uppercase tracking-[0.2em]">Headlines</p>
                            <p className="mt-6 text-lg">Satoshi — clean, geometric body copy for everything else.</p>
                            <p className="text-xs text-ink/50 mt-1 uppercase tracking-[0.2em]">Body text</p>
                        </div>
                    </Reveal>
                </div>
            </main>
        </div>
    );
}
